import { call, put, select, takeEvery } from 'redux-saga/effects';
import { captureException } from '@sentry/nextjs';
import { selectMatchedStrategyCollection } from '../../../../Helpers/strategyCollection.helpers';
import { selectCurrentStepKey } from '../../../../Helpers/clientApiJourneyPosition.helpers';
import ActionTypes from '../../../../Data/ActionTypes';
import FetchStatus from '../../../../Data/FetchStatus';
import { incrementMetric } from '../../../../Services/Datadog';
import { getErrorTypeTag } from '../clientApiError.helpers';
import { getStepApi } from '../clientApiStep.helpers';
import { resolveDependencies } from '../clientApiDependencies.helpers';
import {
  setLocations,
  setLocationsStatus,
  setStepResponse,
} from '../../slices/clientApiJourney';
import { fetchClientApiLocations } from './fetchClientApiLocations';
import {
  convertLocationDistancesToRadiusUnit,
  filterLocationsByRadius,
} from './getClientApiLocations.helpers';

export function* getClientApiLocationsWorker(action) {
  const { postcode, storeRadius, storeRadiusUnit } = action.payload;

  try {
    yield put(setLocationsStatus({ locationsStatus: FetchStatus.IN_PROGRESS }));

    const matchedCollection = yield select(selectMatchedStrategyCollection);
    const stepKey = yield select(selectCurrentStepKey);
    const api = getStepApi({
      collection: matchedCollection,
      stepKey,
      operation: 'list',
    });

    if (!api?.url) {
      throw new Error(`No list operation on strategy step ${stepKey}`);
    }

    const submissionId = yield select((state) => state.submission?.id);
    const token = yield select((state) => state.submission?.token);
    const stepStates = yield select(
      (state) => state.clientApiJourney.stepStates
    );
    const stepResponses = yield select(
      (state) => state.clientApiJourney.stepResponses
    );

    const body = resolveDependencies({
      dependencies: api.dependencies,
      ownKey: stepKey,
      ownInputs: { postcode },
      stepStates,
      stepResponses,
    });

    const { locations, response } = yield call(fetchClientApiLocations, {
      api,
      body,
      submissionId,
      token,
    });

    const locationsInRadiusUnit = convertLocationDistancesToRadiusUnit({
      locations,
      storeRadiusUnit,
    });

    yield put(
      setLocations({
        locations: filterLocationsByRadius({
          locations: locationsInRadiusUnit,
          storeRadius,
        }),
      })
    );
    yield put(setStepResponse({ stepKey, operation: 'list', response }));
    yield put(setLocationsStatus({ locationsStatus: FetchStatus.COMPLETED }));
  } catch (error) {
    incrementMetric({
      name: 'getClientApiLocationsError',
      tags: { type: getErrorTypeTag(error) },
    });
    captureException(error);
    yield put(setLocationsStatus({ locationsStatus: FetchStatus.FAILED }));
  }
}

export default function* getClientApiLocationsSaga() {
  yield takeEvery(
    ActionTypes.GET_CLIENT_API_LOCATIONS,
    getClientApiLocationsWorker
  );
}
